import type {
  InstancePolicy,
  OperatorSupportGrant,
  OperatorTenant,
  OperatorTenantDetail,
  Plan,
} from "@esi/shared";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api.ts";
import { ok } from "../api-errors.ts";

/** Query keys of the console; every one starts with `console` (operators only). */
export const consoleKeys = {
  tenants: ["console", "tenants"] as const,
  tenant: (id: string) => ["console", "tenants", id] as const,
  plans: ["console", "plans"] as const,
  modelConnections: ["console", "model-connections"] as const,
  modelHealth: ["console", "model-health"] as const,
  modelLabs: ["console", "model-labs"] as const,
  policies: ["console", "policies"] as const,
  support: ["console", "support"] as const,
  grant: (id: string) => ["console", "support", id] as const,
  sessions: ["console", "sessions"] as const,
  passkeys: ["console", "account", "passkeys"] as const,
};

const operator = api.api.operator;

export function useTenants() {
  return useQuery({
    queryKey: consoleKeys.tenants,
    queryFn: async (): Promise<OperatorTenant[]> => ok(await operator.tenants.$get()),
    retry: false,
  });
}

export function useTenant(tenantId: string) {
  return useQuery({
    queryKey: consoleKeys.tenant(tenantId),
    queryFn: async (): Promise<OperatorTenantDetail> =>
      ok(await operator.tenants[":tenantId"].$get({ param: { tenantId } })),
    retry: false,
  });
}

export function usePlans() {
  return useQuery({
    queryKey: consoleKeys.plans,
    queryFn: async (): Promise<Plan[]> => ok(await operator.plans.$get()),
    retry: false,
  });
}

export function usePolicies() {
  return useQuery({
    queryKey: consoleKeys.policies,
    queryFn: async (): Promise<InstancePolicy[]> => ok(await operator.policies.$get()),
    retry: false,
  });
}

export function useOwnGrants() {
  return useQuery({
    queryKey: consoleKeys.support,
    queryFn: async (): Promise<OperatorSupportGrant[]> => ok(await operator.support.$get()),
    retry: false,
  });
}

export function useOwnGrant(grantId: string) {
  return useQuery({
    queryKey: consoleKeys.grant(grantId),
    queryFn: async (): Promise<OperatorSupportGrant> =>
      ok(await operator.support[":grantId"].$get({ param: { grantId } })),
    retry: false,
  });
}

/** Whether a grant gives access right now (the server checks it on every request). */
export function grantLive(grant: Pick<OperatorSupportGrant, "status" | "expiresAt">): boolean {
  return (
    grant.status === "active" &&
    grant.expiresAt !== null &&
    Date.parse(grant.expiresAt) > Date.now()
  );
}
